import type { CatalogBlock } from './catalog';
import { STANDARD_SPECS, type ParametricSpec } from './parametric';
import { SYMBOLS } from './symbols';

/**
 * 표준 블록 라이브러리 60종 (작업지시서 P4-07)
 *
 * ── 왜 60종을 미리 넣는가 ────────────────────────────────────────────────
 *
 *   빈 라이브러리로 출시하면 첫 사용자가 곤돌라 하나 놓으려고
 *   블록부터 그려야 한다. 그 순간 제품은 안 쓰이게 된다.
 *   점포 배치 도면 한 장을 그리는 데 필요한 것이 전부 들어 있어야 한다.
 *
 * ── 값의 출처와 신뢰도 ──────────────────────────────────────────────────
 *
 *   ⚠️ 치수는 국내 대형마트에서 통용되는 **모듈 규격**을 따랐고,
 *      단가는 **자리표시자**다. 실제 견적에 쓰기 전에 구매부서 단가로
 *      덮어써야 한다 (관리자 화면 P4-06 에서 수정한다).
 *      이격 거리(clearance)는 통로 폭 검증(P5-02)이 그대로 쓰므로
 *      임의로 낮추면 안 된다 — 소방·피난 검토와 직결된다.
 *
 *   블록 코드는 DB(`cad_block.block_code`)의 기본키다. **바꾸면 기존
 *   도면의 블록 참조가 끊긴다.** 이름은 바꿔도 되고 코드는 못 바꾼다.
 */

type Spec = ParametricSpec;

/** 파라메트릭 스펙 위에 기본값만 갈아끼운 변형을 만든다 */
function withDefaults(base: Spec, defaults: Record<string, number>): Spec {
  return {
    generator: base.generator,
    params: base.params.map((p) =>
      p.key in defaults ? { ...p, default: defaults[p.key]! } : p
    ),
  };
}

const S = STANDARD_SPECS;

/* -------------------------------------------------------------------------- *
 * 매대 (fixture) — 14
 * -------------------------------------------------------------------------- */

const FIXTURE: CatalogBlock[] = [
  {
    code: 'GONDOLA',
    name: '곤돌라 (양면)',
    categoryCode: 'fixture',
    spec: S.gondola,
    defaultLayer: 'F-FIXTURE',
    clearance: { frontMm: 1800, rearMm: 1800 },
    cost: { standardKrw: 480_000, installKrw: 60_000 },
  },
  {
    code: 'GONDOLA-LOW',
    name: '저곤돌라 (양면 1350)',
    categoryCode: 'fixture',
    spec: withDefaults(S.gondola!, { shelfLevels: 4 }),
    defaultLayer: 'F-FIXTURE',
    clearance: { frontMm: 1500, rearMm: 1500 },
    cost: { standardKrw: 410_000, installKrw: 55_000 },
  },
  {
    code: 'WALL-SHELF',
    name: '벽면 진열장',
    categoryCode: 'fixture',
    spec: S.wallshelf,
    defaultLayer: 'F-FIXTURE',
    clearance: { frontMm: 1800 },
    cost: { standardKrw: 320_000, installKrw: 50_000 },
  },
  {
    code: 'WALL-SHELF-HIGH',
    name: '벽면 고단 진열장 (2100)',
    categoryCode: 'fixture',
    spec: withDefaults(S.wallshelf!, { shelfLevels: 7 }),
    defaultLayer: 'F-FIXTURE',
    clearance: { frontMm: 1800 },
    cost: { standardKrw: 390_000, installKrw: 60_000 },
  },
  {
    code: 'ENDCAP',
    name: '엔드매대',
    categoryCode: 'fixture',
    spec: S.endcap,
    defaultLayer: 'F-FIXTURE',
    clearance: { frontMm: 2400, sideMm: 600 },
    cost: { standardKrw: 620_000, installKrw: 80_000 },
  },
  {
    code: 'ENDCAP-ROUND',
    name: '라운드 엔드매대',
    categoryCode: 'fixture',
    spec: withDefaults(S.endcap!, { width: 1500, depth: 900 }),
    defaultLayer: 'F-FIXTURE',
    clearance: { frontMm: 2400, sideMm: 600 },
    cost: { standardKrw: 780_000, installKrw: 100_000 },
  },
  {
    code: 'TABLE-FLAT',
    name: '평대',
    categoryCode: 'fixture',
    spec: S.table,
    defaultLayer: 'F-FIXTURE',
    clearance: { frontMm: 1200, sideMm: 1200, rearMm: 1200 },
    cost: { standardKrw: 340_000, installKrw: 40_000 },
  },
  {
    code: 'TABLE-ISLAND',
    name: '아일랜드 평대',
    categoryCode: 'fixture',
    spec: withDefaults(S.table!, { width: 2400, depth: 1200 }),
    defaultLayer: 'F-FIXTURE',
    clearance: { frontMm: 1500, sideMm: 1500, rearMm: 1500 },
    cost: { standardKrw: 520_000, installKrw: 60_000 },
  },
  {
    code: 'PROMO-STAND',
    name: '행사 매대',
    categoryCode: 'fixture',
    spec: withDefaults(S.table!, { width: 900, depth: 900 }),
    defaultLayer: 'M-MD',
    clearance: { frontMm: 1200, sideMm: 1000, rearMm: 1000 },
    cost: { standardKrw: 180_000, installKrw: 20_000 },
  },
  {
    code: 'DUMP-BIN',
    name: '덤프빈 (원형)',
    categoryCode: 'fixture',
    spec: S.bin,
    defaultLayer: 'M-MD',
    clearance: { frontMm: 1200, sideMm: 1200, rearMm: 1200 },
    cost: { standardKrw: 150_000, installKrw: 15_000 },
  },
  {
    code: 'DUMP-BIN-SQ',
    name: '덤프빈 (사각)',
    categoryCode: 'fixture',
    spec: withDefaults(S.box!, { width: 900, depth: 900 }),
    defaultLayer: 'M-MD',
    clearance: { frontMm: 1200, sideMm: 1200, rearMm: 1200 },
    cost: { standardKrw: 140_000, installKrw: 15_000 },
  },
  {
    code: 'HANGER-RACK',
    name: '행거랙',
    categoryCode: 'fixture',
    spec: withDefaults(S.wallshelf!, { width: 1200, depth: 600, shelfLevels: 4 }),
    defaultLayer: 'F-FIXTURE',
    clearance: { frontMm: 1500 },
    cost: { standardKrw: 260_000, installKrw: 35_000 },
  },
  {
    code: 'MAGAZINE-RACK',
    name: '잡지 진열대',
    categoryCode: 'fixture',
    spec: withDefaults(S.wallshelf!, { width: 900, depth: 350, shelfLevels: 5 }),
    defaultLayer: 'F-FIXTURE',
    clearance: { frontMm: 1200 },
    cost: { standardKrw: 210_000, installKrw: 30_000 },
  },
  {
    code: 'PEG-BOARD',
    name: '후크 진열대',
    categoryCode: 'fixture',
    spec: withDefaults(S.wallshelf!, { width: 900, depth: 350, shelfLevels: 6 }),
    defaultLayer: 'F-FIXTURE',
    clearance: { frontMm: 1200 },
    cost: { standardKrw: 190_000, installKrw: 25_000 },
  },
];

/* -------------------------------------------------------------------------- *
 * 냉장·냉동 (refrig) — 11
 *
 * ★ 전력·배수·발열은 자리표시자가 아니라 설계 판단에 쓰인다.
 *   배수(drain)가 true 인 장비를 배수구 없는 자리에 놓으면 공사가 늘어난다.
 * -------------------------------------------------------------------------- */

const REFRIG: CatalogBlock[] = [
  {
    code: 'SHOWCASE',
    name: '냉장 쇼케이스 (다단)',
    categoryCode: 'refrig',
    spec: S.showcase,
    defaultLayer: 'F-REFRIG',
    clearance: { frontMm: 2400, sideMm: 300, rearMm: 150 },
    cost: { standardKrw: 4_200_000, installKrw: 450_000 },
    utility: { power: 220, water: false, drain: true, heatLoadW: 1800 },
  },
  {
    code: 'SHOWCASE-FZ',
    name: '냉동 쇼케이스 (도어형)',
    categoryCode: 'refrig',
    spec: withDefaults(S.showcase!, { width: 1800, depth: 900 }),
    defaultLayer: 'F-REFRIG',
    clearance: { frontMm: 2400, sideMm: 300, rearMm: 200 },
    cost: { standardKrw: 5_600_000, installKrw: 550_000 },
    utility: { power: 220, water: false, drain: true, heatLoadW: 2600 },
  },
  {
    code: 'MULTI-DECK',
    name: '멀티덱 냉장',
    categoryCode: 'refrig',
    spec: withDefaults(S.showcase!, { width: 3750, depth: 900, shelfLevels: 5 }),
    defaultLayer: 'F-REFRIG',
    clearance: { frontMm: 2400, sideMm: 300, rearMm: 150 },
    cost: { standardKrw: 6_800_000, installKrw: 700_000 },
    utility: { power: 220, water: false, drain: true, heatLoadW: 3200 },
  },
  {
    code: 'REACH-IN',
    name: '리치인 냉장고',
    categoryCode: 'refrig',
    spec: withDefaults(S.showcase!, { width: 2100, depth: 1100, shelfLevels: 5 }),
    defaultLayer: 'F-REFRIG',
    clearance: { frontMm: 1800, sideMm: 200, rearMm: 300 },
    cost: { standardKrw: 3_900_000, installKrw: 400_000 },
    utility: { power: 220, water: false, drain: true, heatLoadW: 1600 },
  },
  {
    code: 'ISLAND-FZ',
    name: '평대형 냉동고',
    categoryCode: 'refrig',
    spec: S.island,
    defaultLayer: 'F-REFRIG',
    clearance: { frontMm: 1500, sideMm: 900, rearMm: 1500 },
    cost: { standardKrw: 3_400_000, installKrw: 380_000 },
    utility: { power: 220, water: false, drain: true, heatLoadW: 2200 },
  },
  {
    code: 'ISLAND-CHILL',
    name: '평대형 냉장고',
    categoryCode: 'refrig',
    spec: withDefaults(S.island!, { width: 2100, doors: 2 }),
    defaultLayer: 'F-REFRIG',
    clearance: { frontMm: 1500, sideMm: 900, rearMm: 1500 },
    cost: { standardKrw: 2_900_000, installKrw: 340_000 },
    utility: { power: 220, water: false, drain: true, heatLoadW: 1700 },
  },
  {
    code: 'ICECREAM-FZ',
    name: '아이스크림 냉동고',
    categoryCode: 'refrig',
    spec: withDefaults(S.island!, { width: 1500, depth: 900, doors: 2 }),
    defaultLayer: 'F-REFRIG',
    clearance: { frontMm: 1200, sideMm: 600, rearMm: 1200 },
    cost: { standardKrw: 1_800_000, installKrw: 200_000 },
    utility: { power: 220, water: false, drain: false, heatLoadW: 1100 },
  },
  {
    code: 'CAKE-CASE',
    name: '제과 쇼케이스',
    categoryCode: 'refrig',
    spec: withDefaults(S.showcase!, { width: 1200, depth: 750, shelfLevels: 3 }),
    defaultLayer: 'F-REFRIG',
    clearance: { frontMm: 1800, sideMm: 200, rearMm: 900 },
    cost: { standardKrw: 3_100_000, installKrw: 320_000 },
    utility: { power: 220, water: false, drain: true, heatLoadW: 1300 },
  },
  {
    code: 'WALK-IN-CHILL',
    name: '워크인 냉장실',
    categoryCode: 'refrig',
    spec: withDefaults(S.box!, { width: 3000, depth: 2400 }),
    defaultLayer: 'F-REFRIG',
    clearance: { frontMm: 1500 },
    cost: { standardKrw: 12_000_000, installKrw: 3_500_000 },
    utility: { power: 380, water: false, drain: true, heatLoadW: 5200 },
  },
  {
    code: 'WALK-IN-FZ',
    name: '워크인 냉동실',
    categoryCode: 'refrig',
    spec: withDefaults(S.box!, { width: 2400, depth: 2400 }),
    defaultLayer: 'F-REFRIG',
    clearance: { frontMm: 1500 },
    cost: { standardKrw: 15_000_000, installKrw: 4_200_000 },
    utility: { power: 380, water: false, drain: true, heatLoadW: 7400 },
  },
  {
    code: 'COND-UNIT',
    name: '실외 응축기',
    categoryCode: 'refrig',
    spec: withDefaults(S.box!, { width: 1200, depth: 800 }),
    defaultLayer: 'M-ELEC',
    clearance: { frontMm: 1000, sideMm: 600, rearMm: 600 },
    cost: { standardKrw: 4_800_000, installKrw: 1_200_000 },
    utility: { power: 380, water: false, drain: false, heatLoadW: 0 },
  },
];

/* -------------------------------------------------------------------------- *
 * 신선 (fresh) — 7
 * -------------------------------------------------------------------------- */

const FRESH: CatalogBlock[] = [
  {
    code: 'MEAT-CASE',
    name: '정육 대면 케이스',
    categoryCode: 'fresh',
    spec: withDefaults(S.showcase!, { width: 3000, depth: 1100, shelfLevels: 3 }),
    defaultLayer: 'F-REFRIG',
    clearance: { frontMm: 2400, sideMm: 300, rearMm: 1200 },
    cost: { standardKrw: 7_200_000, installKrw: 800_000 },
    utility: { power: 220, water: true, drain: true, heatLoadW: 2800 },
  },
  {
    code: 'FISH-CASE',
    name: '수산 대면 케이스',
    categoryCode: 'fresh',
    spec: withDefaults(S.showcase!, { width: 3000, depth: 1100, shelfLevels: 3 }),
    defaultLayer: 'F-REFRIG',
    clearance: { frontMm: 2400, sideMm: 300, rearMm: 1200 },
    cost: { standardKrw: 7_800_000, installKrw: 900_000 },
    utility: { power: 220, water: true, drain: true, heatLoadW: 3100 },
  },
  {
    code: 'SUSHI-CASE',
    name: '초밥 쇼케이스',
    categoryCode: 'fresh',
    spec: withDefaults(S.showcase!, { width: 1800, depth: 900, shelfLevels: 3 }),
    defaultLayer: 'F-REFRIG',
    clearance: { frontMm: 1800, sideMm: 300, rearMm: 900 },
    cost: { standardKrw: 4_100_000, installKrw: 450_000 },
    utility: { power: 220, water: false, drain: true, heatLoadW: 1500 },
  },
  {
    code: 'DELI-COUNTER',
    name: '델리 카운터',
    categoryCode: 'fresh',
    spec: withDefaults(S.counter!, { width: 2100, depth: 1200 }),
    defaultLayer: 'F-FIXTURE',
    clearance: { frontMm: 1800, sideMm: 600, rearMm: 1200 },
    cost: { standardKrw: 2_800_000, installKrw: 400_000 },
    utility: { power: 220, water: true, drain: true, heatLoadW: 900 },
  },
  {
    code: 'MEAT-BLOCK',
    name: '정육 작업대',
    categoryCode: 'fresh',
    spec: withDefaults(S.box!, { width: 1800, depth: 800 }),
    defaultLayer: 'O-OPER',
    clearance: { frontMm: 1200, rearMm: 1200 },
    cost: { standardKrw: 1_400_000, installKrw: 150_000 },
    utility: { power: 220, water: true, drain: true, heatLoadW: 200 },
  },
  {
    code: 'BAKERY-OVEN',
    name: '베이커리 오븐',
    categoryCode: 'fresh',
    spec: withDefaults(S.box!, { width: 1400, depth: 1100 }),
    defaultLayer: 'O-OPER',
    clearance: { frontMm: 1500, sideMm: 300, rearMm: 500 },
    cost: { standardKrw: 9_500_000, installKrw: 1_500_000 },
    utility: { power: 380, water: true, drain: true, heatLoadW: 6000 },
  },
  {
    code: 'FISH-TANK',
    name: '활어 수조',
    categoryCode: 'fresh',
    spec: withDefaults(S.box!, { width: 2400, depth: 900 }),
    defaultLayer: 'F-REFRIG',
    clearance: { frontMm: 1800, rearMm: 900 },
    cost: { standardKrw: 6_500_000, installKrw: 1_800_000 },
    utility: { power: 220, water: true, drain: true, heatLoadW: 1200 },
  },
];

/* -------------------------------------------------------------------------- *
 * 계산·서비스 (checkout) — 7
 * -------------------------------------------------------------------------- */

const CHECKOUT: CatalogBlock[] = [
  {
    code: 'POS-COUNTER',
    name: '계산대',
    categoryCode: 'checkout',
    spec: S.counter,
    defaultLayer: 'F-POS',
    clearance: { frontMm: 1200, sideMm: 900, rearMm: 900 },
    cost: { standardKrw: 2_600_000, installKrw: 300_000 },
    utility: { power: 220, water: false, drain: false, heatLoadW: 150 },
  },
  {
    code: 'POS-EXPRESS',
    name: '소량 계산대',
    categoryCode: 'checkout',
    spec: withDefaults(S.counter!, { width: 1500, depth: 900 }),
    defaultLayer: 'F-POS',
    clearance: { frontMm: 1200, sideMm: 900, rearMm: 900 },
    cost: { standardKrw: 2_100_000, installKrw: 260_000 },
    utility: { power: 220, water: false, drain: false, heatLoadW: 130 },
  },
  {
    code: 'SELF-CHECKOUT',
    name: '셀프 계산대',
    categoryCode: 'checkout',
    spec: withDefaults(S.box!, { width: 700, depth: 900 }),
    defaultLayer: 'F-POS',
    clearance: { frontMm: 1200, sideMm: 600, rearMm: 600 },
    cost: { standardKrw: 8_500_000, installKrw: 600_000 },
    utility: { power: 220, water: false, drain: false, heatLoadW: 250 },
  },
  {
    code: 'KIOSK',
    name: '키오스크',
    categoryCode: 'checkout',
    spec: withDefaults(S.box!, { width: 600, depth: 600 }),
    defaultLayer: 'F-POS',
    clearance: { frontMm: 1200 },
    cost: { standardKrw: 3_200_000, installKrw: 250_000 },
    utility: { power: 220, water: false, drain: false, heatLoadW: 180 },
  },
  {
    code: 'SERVICE-DESK',
    name: '고객서비스 데스크',
    categoryCode: 'checkout',
    spec: withDefaults(S.counter!, { width: 2100, depth: 1200 }),
    defaultLayer: 'F-POS',
    clearance: { frontMm: 1800, rearMm: 1200 },
    cost: { standardKrw: 3_400_000, installKrw: 450_000 },
    utility: { power: 220, water: false, drain: false, heatLoadW: 200 },
  },
  {
    code: 'CART-BAY',
    name: '카트 보관대',
    categoryCode: 'checkout',
    spec: withDefaults(S.box!, { width: 1200, depth: 2400 }),
    defaultLayer: 'O-OPER',
    clearance: { frontMm: 1800 },
    cost: { standardKrw: 600_000, installKrw: 80_000 },
  },
  {
    code: 'PACKING-TABLE',
    name: '사은 포장대',
    categoryCode: 'checkout',
    spec: withDefaults(S.box!, { width: 1500, depth: 700 }),
    defaultLayer: 'O-OPER',
    clearance: { frontMm: 1200, rearMm: 1200 },
    cost: { standardKrw: 450_000, installKrw: 50_000 },
  },
];

/* -------------------------------------------------------------------------- *
 * 물류·창고 (logistics) — 6
 * -------------------------------------------------------------------------- */

const LOGISTICS: CatalogBlock[] = [
  {
    code: 'RACK',
    name: '창고 랙',
    categoryCode: 'logistics',
    spec: S.rack,
    defaultLayer: 'O-OPER',
    clearance: { frontMm: 1200 },
    cost: { standardKrw: 380_000, installKrw: 50_000 },
  },
  {
    code: 'RACK-HEAVY',
    name: '중량 랙',
    categoryCode: 'logistics',
    spec: withDefaults(S.rack!, { width: 3600, depth: 1100, bays: 4, shelfLevels: 5 }),
    defaultLayer: 'O-OPER',
    // 지게차 통로 — 좁히면 파렛트 적재가 불가능해진다
    clearance: { frontMm: 2700 },
    cost: { standardKrw: 720_000, installKrw: 90_000 },
  },
  {
    code: 'PALLET',
    name: '파렛트',
    categoryCode: 'logistics',
    spec: S.pallet,
    defaultLayer: 'O-OPER',
    clearance: { frontMm: 900 },
    cost: { standardKrw: 35_000, installKrw: 0 },
  },
  {
    code: 'ROLL-CAGE',
    name: '롤테이너',
    categoryCode: 'logistics',
    spec: withDefaults(S.box!, { width: 800, depth: 700 }),
    defaultLayer: 'O-OPER',
    clearance: { frontMm: 900 },
    cost: { standardKrw: 180_000, installKrw: 0 },
  },
  {
    code: 'RECEIVING-DESK',
    name: '검수대',
    categoryCode: 'logistics',
    spec: withDefaults(S.box!, { width: 2400, depth: 900 }),
    defaultLayer: 'O-OPER',
    clearance: { frontMm: 1500, rearMm: 1200 },
    cost: { standardKrw: 850_000, installKrw: 120_000 },
    utility: { power: 220, water: false, drain: false, heatLoadW: 150 },
  },
  {
    code: 'BALER',
    name: '폐지 압축기',
    categoryCode: 'logistics',
    spec: withDefaults(S.box!, { width: 1400, depth: 1100 }),
    defaultLayer: 'O-OPER',
    clearance: { frontMm: 1800, sideMm: 600, rearMm: 600 },
    cost: { standardKrw: 7_500_000, installKrw: 900_000 },
    utility: { power: 380, water: false, drain: false, heatLoadW: 400 },
  },
];

/* -------------------------------------------------------------------------- *
 * 설비·안전 (safety) — 8
 *
 * ★ 이 8종은 정적 심볼이다. 규격이 아니라 **기호**가 정보다.
 *   위치가 법정 이격·피난 검토의 입력이 되므로 크기를 임의로 키우면 안 된다.
 * -------------------------------------------------------------------------- */

const SAFETY: CatalogBlock[] = [
  {
    code: 'FIRE-EXT',
    name: '소화기',
    categoryCode: 'safety',
    staticEntities: SYMBOLS.FIRE_EXT!(),
    defaultLayer: 'M-FIRE',
    // 소화기 앞은 비워 둬야 한다 — 적치 금지 구역 검증(P5-03)이 이 값을 쓴다
    clearance: { frontMm: 600 },
    cost: { standardKrw: 45_000, installKrw: 10_000 },
  },
  {
    code: 'FIRE-HYDRANT',
    name: '옥내 소화전',
    categoryCode: 'safety',
    staticEntities: SYMBOLS.FIRE_HYDRANT!(),
    defaultLayer: 'M-FIRE',
    clearance: { frontMm: 1200, sideMm: 300 },
    cost: { standardKrw: 850_000, installKrw: 400_000 },
  },
  {
    code: 'SPRINKLER',
    name: '스프링클러 헤드',
    categoryCode: 'safety',
    staticEntities: SYMBOLS.SPRINKLER!(),
    defaultLayer: 'M-FIRE',
    cost: { standardKrw: 25_000, installKrw: 35_000 },
  },
  {
    code: 'EMERGENCY-EXIT',
    name: '비상구 유도등',
    categoryCode: 'safety',
    staticEntities: SYMBOLS.EMERGENCY_EXIT!(),
    defaultLayer: 'M-FIRE',
    // 피난 동선 확보 — 이 앞을 막으면 소방 검토에서 반려된다
    clearance: { frontMm: 1500 },
    cost: { standardKrw: 120_000, installKrw: 60_000 },
  },
  {
    code: 'AED',
    name: '자동심장충격기',
    categoryCode: 'safety',
    staticEntities: SYMBOLS.AED!(),
    defaultLayer: 'M-FIRE',
    clearance: { frontMm: 900 },
    cost: { standardKrw: 1_800_000, installKrw: 80_000 },
  },
  {
    code: 'ELEC-PANEL',
    name: '분전반',
    categoryCode: 'safety',
    staticEntities: SYMBOLS.ELEC_PANEL!(),
    defaultLayer: 'M-ELEC',
    // 전기설비 작업공간 — 법정 이격이다
    clearance: { frontMm: 1000 },
    cost: { standardKrw: 1_200_000, installKrw: 600_000 },
  },
  {
    code: 'OUTLET',
    name: '콘센트',
    categoryCode: 'safety',
    staticEntities: SYMBOLS.OUTLET!(),
    defaultLayer: 'M-ELEC',
    cost: { standardKrw: 12_000, installKrw: 25_000 },
  },
  {
    code: 'CCTV',
    name: 'CCTV',
    categoryCode: 'safety',
    staticEntities: SYMBOLS.CCTV!(),
    defaultLayer: 'M-ELEC',
    cost: { standardKrw: 180_000, installKrw: 90_000 },
  },
];

/* -------------------------------------------------------------------------- *
 * 건축 (arch) — 7
 * -------------------------------------------------------------------------- */

const ARCH: CatalogBlock[] = [
  {
    code: 'COLUMN',
    name: '기둥 (사각)',
    categoryCode: 'arch',
    spec: S.column,
    defaultLayer: 'A-WALL',
    cost: {},
  },
  {
    code: 'COLUMN-ROUND',
    name: '기둥 (원형)',
    categoryCode: 'arch',
    spec: withDefaults(S.bin!, { diameter: 600 }),
    defaultLayer: 'A-WALL',
    cost: {},
  },
  {
    code: 'DOOR-SWING',
    name: '여닫이문',
    categoryCode: 'arch',
    staticEntities: SYMBOLS.DOOR_SWING!(),
    defaultLayer: 'A-DOOR',
    // 문 열림 궤적 안에는 아무것도 놓을 수 없다
    clearance: { frontMm: 900 },
    cost: { standardKrw: 450_000, installKrw: 200_000 },
  },
  {
    code: 'DOOR-SLIDE',
    name: '자동문',
    categoryCode: 'arch',
    staticEntities: SYMBOLS.DOOR_SLIDE!(),
    defaultLayer: 'A-DOOR',
    clearance: { frontMm: 1500, rearMm: 1500 },
    cost: { standardKrw: 3_200_000, installKrw: 800_000 },
  },
  {
    code: 'SHUTTER',
    name: '셔터',
    categoryCode: 'arch',
    spec: S.shutter,
    defaultLayer: 'A-DOOR',
    clearance: { frontMm: 1200, rearMm: 1200 },
    cost: { standardKrw: 2_400_000, installKrw: 900_000 },
  },
  {
    code: 'STAIR',
    name: '계단',
    categoryCode: 'arch',
    spec: S.stair,
    defaultLayer: 'A-WALL',
    clearance: { frontMm: 1200 },
    cost: {},
  },
  {
    code: 'ELEVATOR',
    name: '엘리베이터',
    categoryCode: 'arch',
    staticEntities: SYMBOLS.ELEVATOR!(),
    defaultLayer: 'A-WALL',
    clearance: { frontMm: 1800 },
    cost: {},
  },
];

/* -------------------------------------------------------------------------- *
 * 합본
 * -------------------------------------------------------------------------- */

export const STANDARD_BLOCKS: CatalogBlock[] = [
  ...FIXTURE,
  ...REFRIG,
  ...FRESH,
  ...CHECKOUT,
  ...LOGISTICS,
  ...SAFETY,
  ...ARCH,
];

/**
 * DB(`cad_block`)를 아직 못 읽었을 때 쓰는 카탈로그.
 *
 * 표준 60종을 그대로 쓴다 — 축소판을 따로 두면 오프라인에서 그린 도면이
 * 온라인에서 "카탈로그에 없는 블록" 으로 잡혀 물량에서 빠진다.
 *
 * ⚠️ 이 상수는 `catalog.ts` 가 아니라 여기에 있어야 한다.
 *    catalog 는 **동작**(삽입·분해·집계), 여기는 **데이터**다.
 *    데이터가 동작을 참조하는 방향만 허용된다. 반대로 걸면 순환 참조가 되고,
 *    ESM 에서는 로딩 순서에 따라 `ReferenceError` 로 터진다.
 */
export const FALLBACK_CATALOG: CatalogBlock[] = STANDARD_BLOCKS;

/** 카테고리 표시 순서·라벨 — DB `cad_block_category` 와 같아야 한다 */
export const BLOCK_CATEGORIES: { code: string; name: string; icon: string }[] = [
  { code: 'fixture', name: '매대', icon: '🛒' },
  { code: 'refrig', name: '냉장·냉동', icon: '❄️' },
  { code: 'checkout', name: '계산·서비스', icon: '💳' },
  { code: 'fresh', name: '신선', icon: '🥩' },
  { code: 'logistics', name: '물류·창고', icon: '📦' },
  { code: 'safety', name: '설비·안전', icon: '🧯' },
  { code: 'arch', name: '건축', icon: '🏗️' },
];
